import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Check, ChevronLeft, ChevronRight, RotateCw } from "lucide-react";

import { cn } from "@/lib/utils";
import type { BuildInfo } from "@/lib/build-info";
import { NAV_HEIGHT_PX } from "../bottom-nav/nav-constants";
import { applyUpdate } from "../app-update-banner/apply-update";
import { useBuildStaleness } from "./use-build-staleness";

/**
 * Identity of the running build, hoisted to module scope so it keeps a stable
 * reference. Vite's `define` is a textual substitution, so reading
 * `__BUILD_INFO__` inline would mint a fresh object on every render, which
 * would re-run the staleness effect — and reset its interval — on every
 * navigation.
 */
const RUNNING_BUILD: BuildInfo = __BUILD_INFO__;

/** How often the relative deploy time is recomputed. */
const CLOCK_TICK_MS = 60 * 1000;

/** Longest we block a tap waiting for a newly found worker to finish installing. */
const INSTALL_TIMEOUT_MS = 10 * 1000;

/**
 * Waits for a worker that is mid-install to finish, so it is genuinely
 * `waiting` by the time we try to activate it.
 *
 * `update()` resolves as soon as the update *job* resolves, which the service
 * worker specification does while the new worker is still `installing` — before
 * workbox has precached a single byte. Handing that registration straight to
 * `applyUpdate` finds nothing waiting, so it falls back to a plain reload and
 * lands on the very assets we are trying to leave.
 */
function waitForInstall(
  registration: ServiceWorkerRegistration,
  timeoutMs: number = INSTALL_TIMEOUT_MS,
): Promise<void> {
  const installing = registration.installing;
  if (!installing) return Promise.resolve();

  return new Promise((resolve) => {
    const listeners = new AbortController();

    // Declared as a hoisted function so it can close over `timer` below.
    function finish() {
      listeners.abort();
      window.clearTimeout(timer);
      resolve();
    }

    installing.addEventListener(
      "statechange",
      () => {
        // Leaving `installing` for any reason ends the wait: `installed` is
        // what we want, and `activated`/`redundant` mean it will never come.
        if (installing.state !== "installing") finish();
      },
      { signal: listeners.signal },
    );

    // Never block the tap indefinitely on a slow precache.
    const timer = window.setTimeout(finish, timeoutMs);
  });
}

/**
 * Loads the newest deployed build.
 *
 * A plain reload is not enough — the service worker would hand the same cached
 * assets straight back. So the newest worker is fetched, allowed to finish
 * installing, then activated through the very path the update bar uses, so both
 * surfaces land on the same build.
 */
async function loadNewestBuild(): Promise<void> {
  const registration =
    (await navigator.serviceWorker?.getRegistration().catch(() => null)) ??
    null;

  if (registration) {
    await registration.update().catch(() => {});
    await waitForInstall(registration);
  }

  applyUpdate(registration);
}

/** Relative deploy time, or `null` when the stamp carries no usable instant. */
function describeAge(builtAt: string): string | null {
  const at = new Date(builtAt);
  return Number.isNaN(at.getTime())
    ? null
    : formatDistanceToNow(at, { addSuffix: true });
}

function BuildStampChipInner({ info }: { info: BuildInfo }) {
  const { stale, checking, confirmed, check } = useBuildStaleness(info);
  const [applying, setApplying] = useState(false);
  const [, tick] = useState(0);
  // Session-scoped on purpose: a reload always brings the chip back. Persisting
  // this would let a tester lose the build stamp without remembering they hid
  // it, which defeats the point of the chip. See [[build-stamp-chip.spec]].
  const [collapsed, setCollapsed] = useState(false);

  // The deploy time is rendered relative, so it has to be recomputed as the
  // session ages — nothing else would re-render this component.
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const buildLabel = info.label ?? "unidentified build";
  const age = describeAge(info.builtAt);

  const handleTap = () => {
    if (stale) {
      setApplying(true);
      void loadNewestBuild();
      return;
    }
    check();
  };

  let content: React.ReactNode;
  if (applying) {
    content = <span>Loading newest…</span>;
  } else if (stale) {
    content = (
      <>
        <RotateCw aria-hidden="true" className="size-3 shrink-0" />
        <span>Update available — tap to load</span>
      </>
    );
  } else if (checking) {
    content = <span>Checking…</span>;
  } else if (confirmed) {
    content = (
      <>
        <Check aria-hidden="true" className="size-3 shrink-0" />
        <span>Up to date</span>
      </>
    );
  } else {
    content = (
      <>
        <span className="truncate">{buildLabel}</span>
        {age && <span className="shrink-0 opacity-70">· {age}</span>}
      </>
    );
  }

  // Shared skin, so the collapsed nub reads as the same object tucked away —
  // including its warning tint, which is the only thing carrying the staleness
  // signal once the chip is collapsed.
  const skin = cn(
    "pointer-events-auto text-[11px] font-medium shadow-sm backdrop-blur transition-colors",
    stale
      ? "border-amber-500/50 bg-amber-400/90 text-amber-950 hover:bg-amber-400"
      : "border-border/60 bg-background/70 text-muted-foreground hover:bg-background/95",
  );

  return (
    // Click-through wrapper: only the chip itself takes pointer events, so a
    // tap aimed at the bottom navigation underneath is never swallowed.
    <div
      className="pointer-events-none fixed left-0 z-[55] flex"
      style={{
        // Clear of the bottom nav rather than over it. The chip is interactive,
        // so overlapping the nav would swallow taps meant for navigation — the
        // click-through wrapper only protects the space *around* the chip.
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${NAV_HEIGHT_PX}px + 0.5rem)`,
      }}
    >
      {collapsed ? (
        // Flush against the viewport edge, so the chip reads as tucked off-screen
        // rather than shrunk in place.
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-expanded={false}
          aria-label="Show build stamp"
          title="Show build stamp"
          className={cn(
            skin,
            // Roomier than the arrow it holds: collapsed, this is the only way
            // back, and it has to stay tappable on a phone.
            "rounded-r-full border border-l-0 py-1.5 pr-2 pl-1",
          )}
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </button>
      ) : (
        <div
          className={cn(
            skin,
            "ml-2 flex max-w-[70vw] items-center rounded-full border",
          )}
        >
          <button
            type="button"
            onClick={handleTap}
            aria-live="polite"
            title={[buildLabel, info.ref, info.sha?.slice(0, 7), info.builtAt]
              .filter(Boolean)
              .join("\n")}
            className="flex min-w-0 items-center gap-1.5 rounded-l-full py-1 pr-1 pl-2.5"
          >
            {content}
          </button>
          {/* A sibling of the status button, not a child: nesting it would make
              tucking the chip away also fire a freshness check or an update. */}
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            aria-expanded
            aria-label="Hide build stamp"
            title="Hide build stamp"
            className="shrink-0 self-stretch rounded-r-full pr-2 pl-1 opacity-70 hover:opacity-100"
          >
            <ChevronLeft aria-hidden="true" className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Dev-only chip naming the build currently running, and warning when a newer
 * one has been deployed.
 *
 * Exists because the update bar is a *push* signal that is legitimately silent
 * in several situations — so its absence cannot tell a tester whether they are
 * current. This chip is the *pull* signal: always readable, and answerable on
 * demand with a tap. See [[build-stamp-chip.spec]].
 *
 * `info` is injectable purely so tests can supply a build identity; production
 * code mounts it bare and it reads the build-time constant.
 */
export function BuildStampChip({
  info = RUNNING_BUILD,
}: {
  info?: BuildInfo;
}) {
  // Fails CLOSED: renders only where the build positively identifies itself as
  // a non-production deploy. `env` is null whenever the build had no Vercel
  // environment to read — a local build, but also a deploy where Vercel's
  // system environment variables were never exposed to the build. Gating on
  // `!== "prod"` would put this chip in front of real users in exactly that
  // case, so absent evidence it shows nothing.
  if (info.env !== "dev") return null;
  return <BuildStampChipInner info={info} />;
}
